import { useCallback, useEffect, useMemo, useState } from 'react';
import Page from '../../../components/page/Page';
import Header from '../../../components/operations/containerProducts/Header';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import useThemeMediaQuery from '../../../../core/hooks/useThemeMediaQuery';
import { useNavigationCore } from '../../../../core/router/navigation';
import { useMeCore } from '../../../../core/modules/me';
import { useCompanyCore } from '../../../../core/modules/company';
import { useParams } from 'react-router-dom';
import { useContainerCore } from '../../../../core/modules/container';
import { useMasterCore } from '../../../../core/modules/master';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface ContainerProductsFilters {
  search?: string;
  product_category_id?: number;
}

const ContainerProducts = () => {
  const { id } = useParams();
  const { user } = useMeCore();
  const { companyId } = useCompanyCore();
  const { products, getContainerProducts } = useContainerCore();
  const [filters, setFilters] = useState<ContainerProductsFilters>({});
  const {
    productStates,
    productUsages,
    productPackagings,
    productFuntionalities,
    productAesthetics,
    productCompletenesses,
    getProductAttributes
  } = useMasterCore();
  const { goToWelcome } = useNavigationCore();

  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const [imageGallery, setImageGallery] = useState<string[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const hasGalleryImages = imageGallery.length > 0;

  const handleOpenImageDialog = useCallback((files: Array<{ URL: string }>, index = 0) => {
    if (!files || files.length === 0) {
      return;
    }
    const urls = files
      .map((file) => file.URL)
      .filter((url): url is string => Boolean(url));
    if (!urls.length) {
      return;
    }
    setImageGallery(urls);
    setSelectedImageIndex(index);
    setIsImageDialogOpen(true);
  }, []);

  const handleCloseImageDialog = useCallback(() => {
    setIsImageDialogOpen(false);
    setImageGallery([]);
    setSelectedImageIndex(0);
  }, []);

  const handlePrevImage = useCallback(() => {
    if (!hasGalleryImages) return;
    setSelectedImageIndex((prev) => (prev - 1 + imageGallery.length) % imageGallery.length);
  }, [hasGalleryImages, imageGallery.length]);

  const handleNextImage = useCallback(() => {
    if (!hasGalleryImages) return;
    setSelectedImageIndex((prev) => (prev + 1) % imageGallery.length);
  }, [hasGalleryImages, imageGallery.length]);

  useEffect(() => {
    if (
      !user ||
      (
        !user.privileges.superAdmin &&
        !user.privileges.readReception &&
        !user.privileges.writeReception
      )
    ) {
      goToWelcome();
    }
  }, [user, goToWelcome]);

  useEffect(() => {
    if (companyId && id) {
      getContainerProducts(companyId, +id);
    }
  }, [companyId, getContainerProducts, id]);

  // Cargar todos los atributos de producto cuando el componente se monte
  useEffect(() => {
    if (companyId) {
      getProductAttributes(companyId, 'state');
      getProductAttributes(companyId, 'usage');
      getProductAttributes(companyId, 'packaging');
      getProductAttributes(companyId, 'functionality');
      getProductAttributes(companyId, 'aesthetic');
      getProductAttributes(companyId, 'completeness');
    }
  }, [companyId, getProductAttributes]);

  // Filtrar productos localmente según los filtros
  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // Filtrar por búsqueda (código o descripción)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((product: any) => {
        const code = (product.product_code || product.code || '').toLowerCase();
        const description = (product.product_description || product.description || product.name || '').toLowerCase();
        return code.includes(searchLower) || description.includes(searchLower);
      });
    }

    // Filtrar por categoría
    if (filters.product_category_id) {
      filtered = filtered.filter((product: any) => {
        return product.product_category_id === filters.product_category_id;
      });
    }

    return filtered;
  }, [products, filters]);

  const columns: GridColDef[] = [
    {
      field: 'product_code',
      headerName: 'SKU',
      minWidth: 200,
      cellClassName: 'flex items-center p-16',
      renderCell: (params) => (
        <div className="px-8 py-4 font-medium">
          {params.row.product_code}
        </div>
      ),
    },
    {
      field: 'product_description',
      headerName: 'Descripción',
      minWidth: 300,
      cellClassName: 'flex items-start p-16',
      renderCell: (params) => (
        <div className="px-8 py-4 text-sm leading-relaxed">
          {params.row.product_description}
        </div>
      ),
    },
    {
      field: 'state_id',
      headerName: 'Estado General',
      minWidth: 150,
      cellClassName: 'flex items-center justify-center p-16',
      renderCell: (params) => {
        const state = productStates.find(s => s.id === params.row.state_id);
        const stateName = state ? state.name : `Estado ${params.row.state_id}`;

        // Determinar el color basado en el nombre del estado
        const getStateColor = (name: string) => {
          const lowerName = name.toLowerCase();
          if (lowerName.includes('excelente') || lowerName.includes('bueno') || lowerName.includes('nuevo') || lowerName.includes('buen')) {
            return 'bg-green-100 text-green-800 border border-green-200';
          } else if (lowerName.includes('regular') || lowerName.includes('aceptable')) {
            return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
          } else if (lowerName.includes('malo') || lowerName.includes('dañado') || lowerName.includes('usado')) {
            return 'bg-red-100 text-red-800 border border-red-200';
          }
          return 'bg-gray-100 text-gray-800 border border-gray-200';
        };

        return (
          <div className={`px-6 py-3 rounded-full text-sm font-semibold ${getStateColor(stateName)}`}>
            {stateName}
          </div>
        );
      },
    },
    {
      field: 'quantity',
      headerName: 'Cantidad',
      minWidth: 120,
      cellClassName: 'flex items-center justify-center p-16',
      renderCell: (params) => (
        <div className="px-8 py-4 font-semibold text-center">
          {params.row.quantity}
        </div>
      ),
    },
    {
      field: 'observation',
      headerName: 'Observación',
      minWidth: 200,
      cellClassName: 'flex items-center p-16',
      renderCell: (params) => (
        <div className="px-8 py-4 text-sm text-gray-700">
          {params.row.observation || '-'}
        </div>
      ),
    },
    {
      field: 'reference',
      headerName: 'Referencia',
      minWidth: 150,
      cellClassName: 'flex items-center p-16',
      renderCell: (params) => (
        <div className="px-8 py-4 text-sm font-mono text-gray-600">
          {params.row.reference || '-'}
        </div>
      ),
    },
    // Columnas ocultas para estados del producto
    {
      field: 'usage_id',
      headerName: 'Uso',
      minWidth: 150,
      hideable: true,
      cellClassName: 'flex items-center p-16',
      renderCell: (params) => {
        const usage = productUsages.find((u: any) => u.id === params.row.usage_id);
        return (
          <div className="px-8 py-4 text-sm">
            {usage ? usage.name : `Uso ${params.row.usage_id}`}
          </div>
        );
      },
    },
    {
      field: 'packaging_id',
      headerName: 'Empaquetado',
      minWidth: 150,
      hideable: true,
      cellClassName: 'flex items-center p-16',
      renderCell: (params) => {
        const packaging = productPackagings.find((p: any) => p.id === params.row.packaging_id);
        return (
          <div className="px-8 py-4 text-sm">
            {packaging ? packaging.name : `Empaquetado ${params.row.packaging_id}`}
          </div>
        );
      },
    },
    {
      field: 'functionality_id',
      headerName: 'Funcionalidad',
      minWidth: 150,
      hideable: true,
      cellClassName: 'flex items-center p-16',
      renderCell: (params) => {
        const functionality = productFuntionalities.find((f: any) => f.id === params.row.functionality_id);
        return (
          <div className="px-8 py-4 text-sm">
            {functionality ? functionality.name : `Funcionalidad ${params.row.functionality_id}`}
          </div>
        );
      },
    },
    {
      field: 'aesthetic_id',
      headerName: 'Estético',
      minWidth: 150,
      hideable: true,
      cellClassName: 'flex items-center p-16',
      renderCell: (params) => {
        const aesthetic = productAesthetics.find((a: any) => a.id === params.row.aesthetic_id);
        return (
          <div className="px-8 py-4 text-sm">
            {aesthetic ? aesthetic.name : `Estético ${params.row.aesthetic_id}`}
          </div>
        );
      },
    },
    {
      field: 'completeness_id',
      headerName: 'Completitud',
      minWidth: 150,
      hideable: true,
      cellClassName: 'flex items-center p-16',
      renderCell: (params) => {
        const completeness = productCompletenesses.find((c: any) => c.id === params.row.completeness_id);
        return (
          <div className="px-8 py-4 text-sm">
            {completeness ? completeness.name : `Completitud ${params.row.completeness_id}`}
          </div>
        );
      },
    },
    {
      field: 'product_files',
      headerName: 'Imágenes',
      minWidth: 120,
      cellClassName: 'flex items-center justify-center p-16',
      renderCell: (params) => (
        <Box className="flex gap-3 px-8 py-4 items-center justify-center">
          {params.row.product_files && params.row.product_files.length > 0 ? (
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                component="img"
                src={params.row.product_files[0].URL}
                alt={`Producto ${params.row.product_code}`}
                sx={{
                  width: 56,
                  height: 56,
                  objectFit: 'cover',
                  borderRadius: 1,
                  border: '2px solid',
                  borderColor: 'grey.200',
                  boxShadow: 1,
                  cursor: 'pointer',
                }}
                onClick={() => handleOpenImageDialog(params.row.product_files, 0)}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            </Box>
          ) : (
            <span className="text-gray-400 text-xs px-4 py-2 bg-gray-50 rounded-lg">Sin imágenes</span>
          )}
        </Box>
      ),
    },
  ];

  return (
    <>
      <Page
        header={<Header filters={filters} onFiltersChange={setFilters} />}
        content={
          <Paper className="flex flex-col flex-auto items-center shadow rounded-2xl overflow-hidden">
            <div className="w-full p-32">
              <DataGrid
                rows={filteredProducts.map((product) => ({ ...product, id: product.id }))}
                columns={columns}
                sx={{ border: 0 }}
                disableColumnFilter
                disableRowSelectionOnClick
                disableMultipleRowSelection
                hideFooter
                getRowHeight={() => 'auto'}
                pagination={undefined}
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'product_description', sort: 'asc' }],
                  },
                  columns: {
                    columnVisibilityModel: {
                      observation: false,
                      usage_id: false,
                      packaging_id: false,
                      functionality_id: false,
                      aesthetic_id: false,
                      completeness_id: false,
                    },
                  },
                }}
              />
            </div>
          </Paper>
        }
        scroll={isMobile ? 'normal' : 'content'}
      />
      {hasGalleryImages && (
        <Dialog
          open={isImageDialogOpen}
          onClose={handleCloseImageDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6">Imágenes del producto</Typography>
            <IconButton onClick={handleCloseImageDialog} aria-label="Cerrar">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box
              position="relative"
              display="flex"
              alignItems="center"
              justifyContent="center"
              minHeight="300px"
            >
              {imageGallery.length > 1 && (
                <IconButton
                  onClick={handlePrevImage}
                  aria-label="Imagen anterior"
                  sx={{ position: 'absolute', left: 16 }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              )}
              <Box
                component="img"
                src={imageGallery[selectedImageIndex]}
                alt={`Imagen ${selectedImageIndex + 1}`}
                sx={{
                  width: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: 2,
                  boxShadow: 3,
                }}
              />
              {imageGallery.length > 1 && (
                <IconButton
                  onClick={handleNextImage}
                  aria-label="Imagen siguiente"
                  sx={{ position: 'absolute', right: 16 }}
                >
                  <ChevronRightIcon />
                </IconButton>
              )}
            </Box>
            {imageGallery.length > 1 && (
              <Box
                mt={2}
                display="flex"
                justifyContent="center"
                gap={1}
                flexWrap="wrap"
              >
                {imageGallery.map((url, idx) => (
                  <Box
                    key={`${url}-${idx}`}
                    component="img"
                    src={url}
                    alt={`Miniatura ${idx + 1}`}
                    onClick={() => setSelectedImageIndex(idx)}
                    sx={{
                      width: 64,
                      height: 64,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor: idx === selectedImageIndex ? 'primary.main' : 'transparent',
                      boxShadow: idx === selectedImageIndex ? 3 : 1,
                      cursor: 'pointer',
                    }}
                  />
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseImageDialog}>Cerrar</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default ContainerProducts;
